/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { client, setUnauthorizedHandler } from '../api/client';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  isAdmin: boolean;
  roles: string[];
  permissions: string[];
}

interface AuthContextType {
  user: User | null;
  login: (token: string, user: User) => void;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');

    const clearSession = () => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    };

    setUnauthorizedHandler(clearSession);

    if (!token) {
      setIsLoading(false);
      return () => setUnauthorizedHandler(null);
    }

    client
      .get('/auth/me')
      .then((res) => {
        localStorage.setItem('user', JSON.stringify(res.data.user));
        setUser(res.data.user);
      })
      .catch(clearSession)
      .finally(() => setIsLoading(false));

    return () => setUnauthorizedHandler(null);
  }, []);

  const login = (token: string, userData: User) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
